"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type LabelHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "@/components/ui/icons";

export type SelectOption = { value: string; label: string };

export function FieldLabel({
  className,
  children,
  ...rest
}: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn(
        "mb-[7px] flex flex-wrap items-center gap-[7px] text-[12.5px] font-semibold tracking-[0.2px] text-ink-4",
        className,
      )}
      {...rest}
    >
      {children}
    </label>
  );
}

export function TextInput({
  className,
  invalid,
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn("field h-[47px] px-[15px]", invalid && "field-invalid", className)}
      {...rest}
    />
  );
}

export function TextArea({
  className,
  invalid,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(
        "field block min-h-[132px] resize-y px-[15px] py-3 leading-[1.55]",
        invalid && "field-invalid",
        className,
      )}
      {...rest}
    />
  );
}

/** Inline validation message under a field. */
export function FieldError({
  id,
  children,
}: {
  id?: string;
  children?: ReactNode;
}) {
  if (!children) return null;
  return (
    <p
      id={id}
      role="alert"
      className="mt-[7px] text-[12px] font-medium leading-[1.4] text-danger"
    >
      {children}
    </p>
  );
}

/** Custom listbox select — field trigger + `.menu-pop` panel (matches LanguageMenu). */
export function SelectInput({
  id,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  invalid = false,
  className,
  "aria-required": ariaRequired,
}: {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  "aria-required"?: boolean | "true" | "false";
}) {
  const autoId = useId();
  const listboxId = `${autoId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selected = options.find((o) => o.value === value);
  const display = selected?.label ?? placeholder ?? "";
  const isPlaceholder = !selected && !!placeholder;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  /** Open the listbox with the current value highlighted. */
  const openMenu = () => {
    setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen(true);
  };

  const selectAt = (index: number) => {
    const opt = options[index];
    /* istanbul ignore next -- `activeIndex` is always clamped to the option list */
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    /* istanbul ignore next -- a disabled <button> does not receive key events */
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setActiveIndex((i) => {
        if (options.length === 0) return -1;
        if (e.key === "ArrowDown") return i < options.length - 1 ? i + 1 : 0;
        return i > 0 ? i - 1 : options.length - 1;
      });
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      if (activeIndex >= 0) selectAt(activeIndex);
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (e.key === "Home" && open) {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === "End" && open) {
      e.preventDefault();
      setActiveIndex(options.length - 1);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative", disabled && "opacity-55")}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        // A listbox trigger is a combobox — `button` does not support
        // aria-required / aria-invalid, and screen readers announce it wrong.
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-required={ariaRequired ?? (required || undefined)}
        aria-invalid={invalid || undefined}
        onClick={() => {
          /* istanbul ignore next -- a disabled <button> does not receive clicks */
          if (disabled) return;
          if (open) setOpen(false);
          else openMenu();
        }}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          "field flex h-[47px] items-center pl-[15px] pr-10 text-left",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          open && "accent-ring",
          invalid && "field-invalid",
          isPlaceholder ? "text-muted-2" : "text-ink",
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate">{display}</span>
      </button>
      <ChevronDown
        size={15}
        className={cn(
          "pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted transition-transform duration-200",
          open && "rotate-180",
        )}
      />

      {open ? (
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-labelledby={id}
          className="menu-pop absolute left-0 right-0 top-[calc(100%_+_6px)] z-[70] max-h-[240px] overflow-y-auto"
        >
          {options.map((opt, index) => {
            const active = opt.value === value;
            const highlighted = index === activeIndex;
            return (
              <button
                key={opt.value === "" ? `__empty-${index}` : opt.value}
                type="button"
                role="option"
                data-index={index}
                aria-selected={active}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectAt(index)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-[11px] px-2.5 py-[9px] text-left text-[13.5px] font-medium text-ink transition-colors",
                  highlighted || active
                    ? "bg-[rgba(26,24,22,0.05)]"
                    : "hover:bg-[rgba(26,24,22,0.05)]",
                )}
              >
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                <span
                  className={cn(
                    "flex w-3.5 flex-none items-center justify-center text-accent",
                    active ? "opacity-100" : "opacity-0",
                  )}
                >
                  <Check size={12} />
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** Custom checkbox row: box + label/badges, keyboard accessible. */
export function CheckboxRow({
  checked,
  onToggle,
  disabled = false,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const toggle = () => {
    if (!disabled) onToggle();
  };

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          toggle();
        }
      }}
      className={cn(
        "-mx-2.5 flex items-start gap-[11px] rounded-xl px-2.5 py-[11px] outline-none transition-colors",
        disabled
          ? "cursor-not-allowed opacity-55"
          : "cursor-pointer hover:bg-[rgba(26,24,22,0.04)]",
      )}
    >
      <span
        className={cn(
          "mt-px box-border flex h-[21px] w-[21px] flex-none items-center justify-center rounded-[7px] border-[1.4px] transition-all",
          checked ? "border-accent bg-accent" : "border-[rgba(26,24,22,0.28)] bg-white",
        )}
        style={
          checked
            ? { boxShadow: "0 1px 3px color-mix(in srgb, var(--accent) 33%, transparent)" }
            : undefined
        }
      >
        {checked ? <Check size={12} className="text-white" /> : null}
      </span>
      <span className="flex flex-wrap items-center gap-[7px] pt-px text-[14px] font-medium leading-[1.4] text-ink">
        {children}
      </span>
    </div>
  );
}
